import { TimeEntry } from '../models/types';
import { db, isEntryUnsynced } from './db';
import { calculateNetWorkDuration } from '../utils/timeUtils';

// Configuration for sync service
export interface SyncConfig {
  /**
   * @deprecated Nothing reads this. There was a "Enable Sync" checkbox; one
   * unlucky tap silently stopped a device syncing — hours, timesheets, manual
   * sync and the project catalog all — with no error shown anywhere, and even
   * disabled the Test Connection button that would have diagnosed it. The
   * control is gone. The field is retained only so configs saved by older
   * builds still parse.
   */
  syncEnabled?: boolean;
  /**
   * @deprecated Sync now goes through this app's own /api/sync proxy, which
   * holds the credentials server-side. Retained so configs saved by older
   * builds still parse; the values are ignored.
   */
  dashboardUrl?: string;
  /** @deprecated The sync token lives in the proxy now, never on the device. */
  apiKey?: string;
  /** @deprecated Tenancy is set by the proxy's COMPANY_ID variable. */
  companyId?: number;
}

// Sync status tracking.
//
// There is deliberately no `pendingEntries` here. It used to exist, was only
// ever assigned 0, and drove the Settings panel's "Pending Entries" readout —
// so the one place an employee looks to ask "did my hours make it?" answered
// "nothing waiting" no matter what was queued. A count that lives in memory
// cannot be right: entries are created and edited by components that never
// touch this module. Call `getPendingCount()` instead; it reads the database.
export interface SyncStatus {
  lastSync: Date | null;
  syncInProgress: boolean;
  lastError: string | null;
}

// Default configuration - can be updated via settings.
// Sync is on out of the box: there is nothing left for an employee to configure,
// so requiring a toggle would only be a way for a device to sit silently unsynced.
let syncConfig: SyncConfig = {
  syncEnabled: true,
};

// Sync status state
let syncStatus: SyncStatus = {
  lastSync: null,
  syncInProgress: false,
  lastError: null,
};

/**
 * Update sync configuration
 */
export const updateSyncConfig = (config: Partial<SyncConfig>): void => {
  syncConfig = { ...syncConfig, ...config };
  localStorage.setItem('syncConfig', JSON.stringify(syncConfig));
};

/**
 * Get current sync configuration
 */
export const getSyncConfig = (): SyncConfig => {
  const stored = localStorage.getItem('syncConfig');
  if (stored) {
    syncConfig = { ...syncConfig, ...JSON.parse(stored) };
  }
  return syncConfig;
};

/**
 * Get current sync status
 */
export const getSyncStatus = (): SyncStatus => {
  return { ...syncStatus };
};

/**
 * Same-origin. The request goes to this app's own Pages Function, which attaches
 * the Cloudflare Access service token and the dashboard's sync token before
 * forwarding. No dashboard URL and no credential is stored on the device.
 */
const SYNC_PATH = '/api/sync/time-entries';
const PROJECTS_PATH = '/api/sync/projects';

const formatDateOnly = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatTimeOnly = (date?: Date): string | undefined => {
  if (!date) return undefined;
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};

/** '2026-07-14' + '15:30' -> a local Date. Null time yields undefined. */
const parseDateAndTime = (date: string, time: string | null): Date | undefined => {
  if (!date || !time) return undefined;
  const [y, m, d] = date.split('-').map(Number);
  const [hh, mm] = time.split(':').map(Number);
  return new Date(y, m - 1, d, hh, mm, 0, 0);
};

const getEntrySyncId = (entry: TimeEntry): string => {
  if (entry.id) {
    return `lite-indexeddb-${entry.id}`;
  }

  return `lite-${entry.name}-${entry.timeIn.getTime()}-${entry.project}-${entry.task || ''}`;
};

const buildSyncPayload = (entries: TimeEntry[], employeeEmail?: string | null) => ({
  batchId: `lite_${new Date().toISOString().replace(/[:.]/g, '-')}`,
  // No filtering here. syncTimeEntries already selected these entries through
  // isEntryUnsynced, and re-filtering would drop tombstones (which have no
  // timeOut) and break the index alignment deliveredEntries relies on.
  entries: entries.map(entry => {
    const common = {
      sourceEntryId: getEntrySyncId(entry),
      deviceEntryId: entry.id ? `indexeddb-${entry.id}` : undefined,
      employeeName: entry.name || 'Anonymous',
      employeeEmail: employeeEmail || undefined,
      // A tombstone needs its date: the dashboard resolves the row by
      // (employee, device id, date), so a dateless delete matches nothing.
      date: formatDateOnly(entry.timeIn),
      lastModified: entry.lastModified?.toISOString(),
    };

    if (entry.deletedAt) {
      return { ...common, deleted: true as const };
    }

    return {
      ...common,
      project: entry.project || 'Default Project',
      task: entry.task || undefined,
      timeIn: formatTimeOnly(entry.timeIn),
      timeOut: formatTimeOnly(entry.timeOut),
      hours: calculateNetWorkDuration(entry) / 60,
      location: entry.location || undefined,
      notes: entry.notes || undefined,
    };
  }),
});

// Authentication and tenancy are added by the proxy. The device code is the
// one thing this side contributes: an identifier saying which employee this
// phone is, which the dashboard validates on every request.
//
// `codeOverride` lets a caller supply the code explicitly instead of reading
// it from storage — needed for the pre-enrollment reconciliation batch,
// which must go out with the code BEFORE db.setEnrollmentCode() has run
// (see enrollmentService.completeEnrollment). Every ordinary sync omits it
// and falls back to db.getEnrollmentCode() unchanged.
const getAuthHeaders = async (codeOverride?: string): Promise<Record<string, string>> => {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const code = codeOverride ?? (await db.getEnrollmentCode());
  if (code) headers['x-device-code'] = code;
  return headers;
};

/**
 * Sync time entries to dashboard
 */
export const syncTimeEntries = async (
  entries: TimeEntry[],
  options: { preEnrollment?: boolean; deviceCode?: string } = {}
): Promise<{ success: boolean; message: string; conflicts?: SyncConflict[] }> => {
  if (syncStatus.syncInProgress) {
    return { success: false, message: 'Sync already in progress' };
  }

  syncStatus.syncInProgress = true;
  syncStatus.lastError = null;

  // Captured before the request goes out, not after the response comes back:
  // an edit that lands mid-flight (after this batch's payload is built but
  // before the response returns) must still look unsynced afterward, or it
  // is silently never resent. Stamping syncedAt with a pre-request timestamp
  // guarantees lastModified > syncedAt for any such edit.
  const syncStartedAt = new Date();

  try {
    // Drop anything the dashboard already has. This filter lives HERE, not at
    // the call sites, because the call sites are precisely where it went
    // missing: WeeklyTimesheet's "Sync to Dashboard" button and its
    // signed-timesheet submit both hand over every entry in the displayed
    // week, straight from a Dexie date-range query with no syncedAt test of
    // any kind. Once a device is enrolled the dashboard attributes by the
    // employee_id behind the code, so a re-sent already-reconciled entry
    // matches neither the device-id key nor the natural key (both are scoped
    // by employee_id, and employee_id is exactly what enrollment changes) and
    // is INSERTED as a second copy — company hours double and the enrolled
    // employee's payroll picks up hours already paid under the pre-enrollment
    // name fragment. Enforcing it inside this function closes the whole class
    // instead of two known call sites.
    //
    // autoSync passes db.getUnsyncedEntries() output, and the enrollment
    // handshake's preEnrollment reconciliation batch is built from that same
    // call, so for both this filter is a no-op by construction — it cannot
    // strand the reconciliation batch.
    const pending = entries.filter(isEntryUnsynced);

    const payload = buildSyncPayload(pending, await db.getUserEmail());
    const body = options.preEnrollment ? { ...payload, preEnrollment: true } : payload;

    if (payload.entries.length === 0) {
      // "Already delivered" is a SUCCESS, not a failure. autoSync runs on app
      // open and after every clock-out, so by the time an employee taps "Sync
      // to Dashboard" or submits a signed timesheet, everything is normally
      // already synced. Reporting that as a failure would tell them their hours
      // never reached the office during exactly the weeks that matter most.
      // Having nothing completed to send at all is still a failure — there the
      // employee has to do something (clock out) before sync can work.
      return entries.some((entry) => entry.timeOut)
        ? {
            success: true,
            message: 'Nothing new to sync — these entries have already been sent to the dashboard.'
          }
        : {
            success: false,
            message: 'No completed time entries to sync. Clock out first, then try again.'
          };
    }

    const response = await fetch(SYNC_PATH, {
      method: 'POST',
      headers: await getAuthHeaders(options.deviceCode),
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const details = await response.text().catch(() => '');

      // A revoked code is dead; clear it so the device re-prompts instead of
      // retrying forever with something the dashboard will never accept.
      if (response.status === 403 && details.includes('enrollment_revoked')) {
        await db.clearEnrollmentCode();
        throw new Error('This device\'s code was turned off. Ask the office for a new one.');
      }

      if (response.status === 403 && details.includes('enrollment_required')) {
        throw new Error('This device needs to be set up with an enrollment code.');
      }

      throw new Error(`HTTP ${response.status}${details ? `: ${details}` : ` ${response.statusText}`}`);
    }

    const result = await response.json();

    // Update sync status
    syncStatus.lastSync = new Date();
    syncStatus.lastError = null;

    // Mark as synced ONLY the rows the dashboard says it actually saved. A 200
    // does not mean the whole batch landed: the endpoint answers 200 with
    // `success: false` and a per-row `status: 'failed'` for anything it could
    // not write (an unparseable date, a NOT NULL violation) — see
    // functions/api/sync/time-entries.ts. Stamping the whole batch used to be
    // merely noisy, because a failed row simply kept re-sending; with the
    // syncedAt predicate above it is fatal, because the row would look
    // delivered forever and silently disappear from payroll.
    const { delivered, deleted, locked } = classifyResults(pending, payload.entries, result);
    await markEntriesAsSynced(delivered, syncStartedAt);

    // The dashboard has removed its copy, so the tombstone has done its job.
    for (const entry of deleted) {
      if (entry.id) await db.timeEntries.delete(entry.id);
    }

    // Refused: the office's record is authoritative. Take its values so the
    // phone and the dashboard cannot disagree, and stamp it synced so it stops
    // going out on every sync forever.
    const conflicts: SyncConflict[] = [];
    for (const { entry, row } of locked) {
      const server = row.serverEntry;
      if (entry.id && server) {
        // A single timestamp for both fields: markEntriesAsSynced's own
        // comment above documents why lastModified must never exceed
        // syncedAt — two independent `new Date()` calls straddling a
        // millisecond boundary would violate exactly that invariant and make
        // the just-reverted entry look unsynced again immediately.
        const now = new Date();
        const serverTimeIn = parseDateAndTime(server.date, server.timeIn);
        await db.timeEntries.update(entry.id, {
          // timeIn is the table's index. A Dexie update setting a property to
          // `undefined` DELETES that property, and a row with no timeIn is
          // invisible to every `where('timeIn')` query in the app — it would
          // vanish from the timesheet, the exports and the totals with no way
          // left to reach it. The server can legitimately hand back a null
          // time_in (an open shift, or an office-entered row), so only take
          // the server's value when there is one and keep ours otherwise.
          ...(serverTimeIn ? { timeIn: serverTimeIn } : {}),
          timeOut: parseDateAndTime(server.date, server.timeOut),
          location: server.location ?? undefined,
          notes: server.notes ?? undefined,
          deletedAt: undefined,
          syncedAt: now,
          lastModified: now,
        });
      }
      conflicts.push({ date: server?.date ?? '', reason: row.reason });
    }

    // Deliberately still `success: true`. The failed rows stay unstamped and
    // will go out again on the next autoSync, which is the pre-existing
    // behavior; failing the whole call instead would also block the enrollment
    // handshake forever on a single permanently-unsavable row.
    const rejected = payload.entries.length - delivered.length - deleted.length - locked.length;

    return {
      success: true,
      message: `Successfully synced ${result.total ?? payload.entries.length} entries (${result.newEntries ?? 0} new, ${result.updatedEntries ?? 0} updated)`
        + (rejected > 0 ? `. ${rejected} could not be saved and will be retried.` : ''),
      ...(conflicts.length > 0 ? { conflicts } : {}),
    };

  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown sync error';
    syncStatus.lastError = errorMessage;
    
    return { 
      success: false, 
      message: `Sync failed: ${errorMessage}` 
    };
  } finally {
    syncStatus.syncInProgress = false;
  }
};

/** A row the dashboard refused because it is already attached to an invoice or a payroll run. */
export type SyncConflict = { date: string; reason: 'invoiced' | 'paid' };

/**
 * What did the dashboard do with each entry we sent?
 *
 * The response shape is defined by functions/api/sync/time-entries.ts:
 *
 *   { success, batchId, total, newEntries, updatedEntries, failed,
 *     results: [{ sourceEntryId, status: 'inserted'|'updated'|'deleted'|'locked'|'failed',
 *                  reason?, serverEntry?, error? }],
 *     errors? }
 *
 * The handler loops the submitted entries in order and pushes exactly one
 * `results` element per entry, so position i in `results` is position i in the
 * payload — index correlation is exact, not a guess. `sourceEntryId` is echoed
 * back on both sides, so it is checked as an alignment guard.
 *
 * If the shapes don't line up at all (an older dashboard build that predates
 * `results`), fall back to the previous all-or-nothing behavior rather than
 * stranding every entry on this device forever.
 */
const classifyResults = (
  sent: TimeEntry[],
  payloadEntries: Array<{ sourceEntryId: string }>,
  result: any
): { delivered: TimeEntry[]; deleted: TimeEntry[]; locked: Array<{ entry: TimeEntry; row: any }> } => {
  const rows: Array<{ sourceEntryId?: string; status?: string }> =
    Array.isArray(result?.results) ? result.results : [];

  // Without a row per sent entry we cannot tell which is which. Fall back to
  // the old behaviour: treat everything as delivered rather than guessing.
  if (rows.length !== payloadEntries.length || sent.length !== payloadEntries.length) {
    return { delivered: sent, deleted: [], locked: [] };
  }
  const aligned = rows.every(
    (row, i) => !row?.sourceEntryId || row.sourceEntryId === payloadEntries[i].sourceEntryId
  );
  if (!aligned) return { delivered: sent, deleted: [], locked: [] };

  const delivered: TimeEntry[] = [];
  const deleted: TimeEntry[] = [];
  const locked: Array<{ entry: TimeEntry; row: any }> = [];

  sent.forEach((entry, i) => {
    const status = rows[i]?.status;
    if (status === 'inserted' || status === 'updated') delivered.push(entry);
    else if (status === 'deleted') deleted.push(entry);
    else if (status === 'locked') locked.push({ entry, row: rows[i] });
  });

  return { delivered, deleted, locked };
};

/**
 * Stamp entries as delivered. This sets syncedAt and deliberately does NOT
 * touch lastModified: the old version bumped lastModified on every sync, so
 * an entry's lastModified was always newer than the last sync time and nothing
 * ever stopped looking unsynced.
 *
 * `syncedAt` is passed in rather than computed here — it must be the time the
 * request was SENT, not the time this function happens to run (after the
 * response comes back). Using post-response time would let an edit that
 * lands mid-flight get stamped with a syncedAt newer than its own
 * lastModified, making it look synced when its content was never actually
 * sent.
 */
const markEntriesAsSynced = async (entries: TimeEntry[], syncedAt: Date): Promise<void> => {
  for (const entry of entries) {
    if (entry.id) {
      await db.timeEntries.update(entry.id, { syncedAt });
    }
  }
};

/** One project as the dashboard reports it — see functions/api/sync/projects.ts. */
interface RemoteProject {
  id: number;
  name: string;
  location: string | null;
  active: boolean;
  tasks: string[];
}

/**
 * Pull the office's job and task catalog onto this device.
 *
 * Dashboard-authoritative, but ADDITIVE. A project is created locally the
 * moment an employee types it and only reaches the dashboard when a time entry
 * using it syncs, so "the dashboard did not mention it" does not mean "it was
 * deleted" — it usually means "it has not arrived yet". Only an explicit
 * active:false prunes.
 *
 * Tasks are unioned and never pruned: the dashboard's `tasks` table has no
 * status column, so it cannot express that a task was retired.
 *
 * Never throws. This runs in an app-open effect, and clocking in must not
 * depend on the office being reachable.
 */
export const syncProjectsFromDashboard = async (): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await fetch(PROJECTS_PATH, {
      method: 'GET',
      headers: await getAuthHeaders(),
    });

    if (!response.ok) {
      const details = await response.text().catch(() => '');
      const message = `HTTP ${response.status}${details ? `: ${details}` : ''}`;
      console.error('Project sync failed:', message);
      return { success: false, message };
    }

    const remote = (await response.json()) as RemoteProject[];
    if (!Array.isArray(remote)) {
      console.error('Project sync failed: Unexpected response from the dashboard', remote);
      return { success: false, message: 'Unexpected response from the dashboard' };
    }

    let added = 0;
    let updated = 0;
    let removed = 0;

    // The dashboard does not enforce name uniqueness on `projects` (the only
    // UNIQUE constraint in schema/d1.sql is on `clients`), and neither
    // createJob nor POST /api/projects checks for an existing name before
    // insert. So a repeat customer — last year's "Miller Kitchen" retired
    // (status='inactive'; a job with time entries is deactivated, never
    // deleted) and a new "Miller Kitchen" created this year — makes this
    // response carry TWO rows with the same name, one active:true and one
    // active:false, with `ORDER BY name` giving no tiebreak between them.
    // Deleting on the retired row's name — in either arrival order — would
    // wipe out the live row's locally-typed tasks (if the retired row is
    // processed first, the live row is then re-added fresh) or delete the
    // job's local record outright (if the retired row is processed second).
    // Precomputing which names are live IN THIS RESPONSE and skipping the
    // delete for any of them makes the merge order-independent. Do not
    // simplify this away — the bug it prevents is silent, order-dependent
    // local data loss.
    const activeNames = new Set(
      remote.filter((r) => r && r.name && r.active !== false).map((r) => r.name)
    );

    for (const item of remote) {
      if (!item || !item.name) continue;

      const existing = await db.projects.where('name').equals(item.name).first();

      // Only an explicit false prunes. A missing or malformed `active` means
      // the office did not say "retired" — and the cost of guessing wrong is
      // deleting the employee's own job.
      if (item.active === false) {
        if (activeNames.has(item.name)) continue; // a live job of this name exists in this response
        if (existing?.id) {
          await db.projects.delete(existing.id);
          removed++;
        }
        continue;
      }

      const remoteTasks = Array.isArray(item.tasks) ? item.tasks : [];

      if (!existing) {
        await db.projects.add({
          name: item.name,
          tasks: remoteTasks,
          location: item.location ?? undefined,
          remoteId: item.id,
        });
        added++;
        continue;
      }

      const mergedTasks = [...(existing.tasks || [])];
      for (const task of remoteTasks) {
        if (!mergedTasks.includes(task)) mergedTasks.push(task);
      }

      await db.projects.update(existing.id!, {
        tasks: mergedTasks,
        location: item.location ?? existing.location,
        remoteId: item.id,
      });
      updated++;
    }

    return {
      success: true,
      message: `Projects updated (${added} added, ${updated} changed, ${removed} removed)`,
    };
  } catch (error) {
    console.error('Project sync failed:', error);
    return {
      success: false,
      message: (error as Error).message || 'Could not reach the dashboard',
    };
  }
};

/**
 * Auto-sync pending entries (called periodically)
 */
export const autoSync = async (): Promise<void> => {
  if (syncStatus.syncInProgress) return;

  try {
    const unsynced = await db.getUnsyncedEntries();
    if (unsynced.length > 0) {
      await syncTimeEntries(unsynced);
    }
  } catch (error) {
    console.error('Auto-sync failed:', error);
  }
};

/**
 * How many completed entries are waiting to reach the dashboard, so a
 * silently failing device (bad code, dead signal) is visible in the UI
 * instead of just quietly falling behind.
 */
export const getPendingCount = async (): Promise<number> => {
  const unsynced = await db.getUnsyncedEntries();
  return unsynced.length;
};

/**
 * Test connection to dashboard
 */
export const testConnection = async (): Promise<{ success: boolean; message: string }> => {
  try {
    // OPTIONS exercises the whole chain — proxy, Access service token, dashboard
    // — but the dashboard answers it before its handler runs, so a connection
    // test writes nothing. The old /api/health probe is gone: that path sits
    // outside the Access Service Auth policy and a device cannot reach it.
    const response = await fetch(SYNC_PATH, {
      method: 'OPTIONS',
    });

    if (response.ok) {
      return { success: true, message: 'Connection successful. Use Weekly Timesheet > Sync to Dashboard to submit completed entries.' };
    } else {
      return { success: false, message: `Connection failed: ${response.status}` };
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    return { success: false, message: `Connection failed: ${errorMessage}` };
  }
};
